<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Dual Phoenix Fire Claws Werebear Druid Build Guide</h1>
    <p>The Dual Phoenix Fireclaws Werebear Druid is an explosive melee powerhouse that leverages Fire Claws for devastating fire-based attacks while benefiting from the dual Phoenix Runeword setup for massive fire damage amplification and life sustain. This build primarily utilizes the Fire Claws skill for attacks, significantly enhancing fire damage through dual Phoenix Runeword weapons. In Werebear form, the Druid gains immense health and defensive capabilities, making it an exceptional tanky damage dealer.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-fireclaws-dru.png">
    </div>
    
    <h2>Recommended Gear</h2>
    <Equipments
      class="right"
      :left-weapon="weapon"
      :right-weapon="shield"
      :helm="helm"
      :armor="armor"
      :gloves="gloves"
      :belt="belt"
    />
    <h3>Weapons</h3>
    <ul>
        <li><strong>Phoenix</strong> Runeword - +Fire Claws damage, Redemption Aura for sustain</li>
    </ul>

    <h3>Shield</h3>
    <ul>
        <li><strong>Phoenix</strong> Runeword - +Fire Claws damage, Redemption Aura for sustain</li>
    </ul>
    
    <h3>Helmet</h3>
    <ul>
        <li><strong>Metamorphosis</strong> (enhances attack bonuses)</li>
        <li><strong>Jalal's Mane</strong> (High resistance and skill bonuses)</li>
    </ul>
    
    <h3>Armor</h3>
    <ul>
        <li>4-socket armor with <strong>4 fire Rainbow Facets</strong> (Maximize fire damage)</li>
        <li><strong>Chains of Honor</strong> (Resistances and skill bonuses)</li>
    </ul>

    <h3>Gloves</h3>
    <ul>
        <li><strong>Laying of Hands</strong> – Increased damage, especially against demons.</li>
    </ul>
    
    <h3>Belt</h3>
    <ul>
        <li><strong>Arachnid Mesh</strong> (+1 Skills and Faster Cast Rate)</li>
        <li><strong>Verdungo’s Hearty Cord</strong> (Survivability boost)</li>
    </ul>
    
    <h3>Rings</h3>
    <ul>
        <li>Bul-Kathos' Wedding Band – +1 Skills and Life Leech</li>
        <li><strong>Raven Frost</strong> (Cannot be frozen, attack rating boost)</li>
    </ul>
    
    <h3>Amulet</h3>
    <ul>
        <li><strong>Highlord’s Wrath</strong> (Attack speed and deadly strike)</li>
    </ul>

    <h3>Inventory</h3>
    <ul>
        <li><strong>Flame Rift</strong> – Used to break fire immunities of monsters.</li>
    </ul>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <h2>Core Skill Allocation</h2>
    <div class="m-3 skills-tab right">
      <b-tabs small content-class="m-2" class="tab-text">
        <b-tab title="Elemental">
          <img class="w-100" src="@/assets/builds/skills-fireclaws-dru-1.png">
        </b-tab>
        <b-tab title="Shape Shifting" active>
          <img class="w-100" src="@/assets/builds/skills-fireclaws-dru-2.png">
        </b-tab>
        <b-tab title="Summoning">
          <img class="w-100" src="@/assets/builds/skills-fireclaws-dru-3.png">
        </b-tab>
      </b-tabs>
    </div>
    <ul>
        <li><strong>Fire Claws</strong> - 20 points (Primary attack skill)</li>
        <li><strong>Lycanthropy</strong> - 20 points (Increases life and transformation duration)</li>
        <li><strong>Werebear</strong> - 1 point (Provides additional defense and stun capability)</li>
        <li><strong>Molten Boulder</strong> - 20 points (Fire Claws synergy for extra fire damage)</li>
        <li><strong>Firestorm</strong> - 20 points (Another Fire Claws synergy)</li>
        <li><strong>Feral Rage</strong> - 1 point (Boosts attack speed)</li>
        <li><strong>Shock Wave</strong> - 1 point (Stuns enemies for crowd control)</li>
        <li>Remaining points can be allocated to <strong>Maul</strong> to enhance physical damage output.</li>
    </ul>
    
    <h2>Attribute Allocation</h2>
    <ul>
        <li><strong>Strength</strong>: Just enough for gear</li>
        <li><strong>Dexterity</strong>: Just enough for gear</li>
        <li><strong>Vitality</strong>: As much as possible</li>
        <li><strong>Energy</strong>: No points</li>
    </ul>

    <h2>Mercenary Choice</h2>
    <p>Act 2 Blessed Aim Aura Mercenary (Enhances attack rating)</p>
    <ul>
        <li><strong>Weapon:</strong> Infinity – Lowers enemy lightning resistance</li>
        <li><strong>Helmet</strong>: Andariel’s Visage (High attack speed and life leech)</li>
        <li><strong>Armor</strong>: Fortitude (Damage boost and resistances)</li>
    </ul>
    
    <h2>Combat Strategy</h2>
    <ol>
        <li><strong>Activate Metamorphosis Buff</strong> (if using Metamorphosis): Transform into a Werewolf and hit to grant the Werewolf mark (repeat every 3 minutes).</li>
        <li><strong>Switch to Werebear</strong>: Transform into a Werebear.</li>
        <li><strong>Initiate control</strong>: Use Shock Wave to stun enemies.</li>
        <li><strong>Fire Claws burst</strong>: Deal massive fire damage with Fire Claws.</li>
        <li><strong>Maintain sustain</strong>: Rely on Phoenix’s Redemption Aura for auto-healing.</li>
    </ol>

    <p>The Dual Phoenix Fireclaws Werebear Druid excels in high attack speed, survivability, and devastating fire damage, making it an excellent melee powerhouse for Hell difficulty.</p>

  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'FireclawsDru',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-crystal-sword',
        'width': 80,
        'title': 'Phoenix Runeword',
        'runes': 'Vex + Vex + Lo + Jah',
        'detail': [
          '100% Chance To Cast level 40 Blaze When You Level Up',
          '40% Chance To Cast Level 22 Firestorm On Striking',
          'Level 10-15 Redemption Aura When Equipped',
          '+350-400% Enhanced Damage',
          "Ignore Target's Defense",
          '14% Mana Stolen Per Hit',
          '-28% To Enemy Fire Resistance',
          '20% Deadly Strike',
          '+350-400 Defense Vs. Missile',
          '+15-21 Fire Absorb',
        ],
      }
    },

    shield () {
      return {
        'img': 'eq-monarch',
        'width': 80,
        'title': 'Phoenix Runeword',
        'runes': 'Vex + Vex + Lo + Jah',
        'detail': [
          '100% Chance To Cast level 40 Blaze When You Level-up', 
          '40% Chance To Cast Level 22 Firestorm On Striking',
          'Level 10-15 Redemption Aura When Equipped',
          '+350-400 Defense Vs. Missile', 
          '+350-400% Enhanced Damage',
          '-28% To Enemy Fire Resistance',
          '+50 To Life',
          '+5% To Maximum Lightning Resist',
          '+10% To Maximum Fire Resist',
          '+15-21 Fire Absorb',
        ],
      }
    },

    helm () {
      return {
        'img': 'eq-wolf-head',
        'width': 75,
        'title': "Metamorphosis Runeword",
        'runes': 'Io + Cham + Fal',
        'detail': [
          'Werewolf strikes grant Mark for 180 seconds', 
          '(Mark of the Wolf) +30% Bonus to Attack Rating',
          '(Mark of the Wolf) Increase Maximum Life 40%',
          'Werebear strikes grant Mark for 180 seconds', 
          '(Mark of the Bear) +25% Attack Speed',
          '(Mark of the Bear) Physical Damage Received Reduced by 20%',
          '+5 to Shape Shifting Skills (Druid only)',
          '+25% Chance of Crushing Blow',
          '+50-80% Enhanced Defense',
          '+10 to Strength',
          '+10 to Vitality',
          'All Resistances +10',
          'Cannot be Frozen',
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Chains of Honor',
        'runes': 'Dol + Um + Ber + Ist',
        'detail': [
          '+2 To All Skills',
          '+200% Damage To Demons',
          '+100% Damage To Undead',
          '8% Life Stolen Per Hit',
          '+70% Enhanced Defense',
          '+20 To Strength',
          'Replenish Life +7',
          'All Resistances +65',
          'Damage Reduced By 8%',
          '25% Better Chance of Getting Magic Items',
        ],
      }
    },

    gloves () {
      return {
        'img': 'eq-laying-of-hands',
        'width': 70,
        'title': "Laying of Hands",
        'detail': [
          'Defense: 79-87', 
          '+25 Defense',
          '+20% Increased Attack Speed',
          '+350% Damage To Demons', 
          'Fire Resist +50%',
          '10% Chance To Cast Level 3 Holy Bolt On Striking',
        ],
      }
    },

    belt () {
      return {
        'img': 'eq-arachnid-mesh',
        'width': 75,
        'title': 'Arachnid Mesh',
        'detail': [
          'Defense: 119-138', 
          '+90-120% Enhanced Defense',
          'Slows Target By 10%',
          '+1 To All Skills', 
          '+20% Faster Cast Rate',
          'Increases Maximum Mana 5%',
          'Level 3 Venom (11 Charges)',
        ],
      }
    },
  }
}
</script>
